import { useState, useCallback, useRef } from "react";
import React from "react";
import ConfirmDialog from "./ConfirmDialog";

/**
 * Promise-based confirmation dialog. Replaces native `window.confirm()` with
 * a polished modal that matches the rest of Chariot's UI. The Promise resolves
 * to `true` when the user confirms and `false` on cancel/ESC/backdrop-click.
 *
 * Migration is nearly mechanical:
 *
 *   // BEFORE
 *   if (!window.confirm("Delete this?")) return;
 *   await doDelete();
 *
 *   // AFTER
 *   const { confirm, dialog } = useConfirm();
 *   //   …in handler:
 *   if (!(await confirm({ message: "Delete this?" }))) return;
 *   await doDelete();
 *   //   …in JSX:
 *   {dialog}
 *
 * Options to confirm():
 *   title       — headline (default: "Are you sure?")
 *   message     — body explanation (required for clarity)
 *   confirmText — primary button label (default: "Confirm")
 *   cancelText  — secondary button label (default: "Cancel")
 *   variant     — "danger" (red, default) | "primary" (the brand color)
 *   input       — { label, placeholder, required, maxLength }: ask for a
 *                 text answer too (a reason, say); the Promise then resolves
 *                 to the trimmed answer, or false on cancel
 *   altText     — a third choice (e.g. "Don't save" beside Save and Keep
 *                 editing); the Promise resolves to "alt" when it's picked
 *
 * Returns:
 *   confirm(opts) → Promise<boolean | string>  (true, false, the answer, or "alt")
 *   dialog        → React element to render (place anywhere in your JSX tree)
 */
export default function useConfirm() {
  const [state, setState] = useState(null);
  // Hold the resolver across renders so the in-flight Promise can be settled
  // by user interaction. Cleared after each resolution.
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        title: opts.title || "Are you sure?",
        message: opts.message || "",
        confirmText: opts.confirmText || "Confirm",
        cancelText: opts.cancelText || "Cancel",
        variant: opts.variant || "danger",
        input: opts.input || null,
        altText: opts.altText || null,
        value: "",
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (resolverRef.current) {
      resolverRef.current(state?.input ? String(state.value || "").trim() : true);
      resolverRef.current = null;
    }
    setState(null);
  }, [state]);
  const handleValueChange = useCallback((value) => setState((s) => (s ? { ...s, value } : s)), []);

  const handleAlt = useCallback(() => {
    if (resolverRef.current) {
      resolverRef.current("alt");
      resolverRef.current = null;
    }
    setState(null);
  }, []);

  const handleCancel = useCallback(() => {
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
    setState(null);
  }, []);

  const dialog = (
    <ConfirmDialog
      open={!!state}
      title={state?.title}
      message={state?.message}
      confirmText={state?.confirmText}
      cancelText={state?.cancelText}
      variant={state?.variant}
      input={state?.input}
      altText={state?.altText}
      onAlt={handleAlt}
      value={state?.value}
      onValueChange={handleValueChange}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { confirm, dialog };
}
